<template>
  <div>
    <div
      class="action-circle"
      @click="isAlertOpen = true"
    >
      <i class="fas fa-bell" />
    </div>
    <Modal
      ref="alertModal"
      v-model="isAlertOpen"
      width="800"
      :styles="{ top: '36px' }"
      scrollable
      class="alert-modal"
    >
      <div
        slot="header"
        class="modal-header"
      >
        Daily Alerts Settings (daily at 3am PST)
      </div>

      <div style="position: relative">
        <Spin
          v-if="initialLoading"
          size="large"
          fix
        />
        <div
          v-if="!Object.keys(regionAlertMap).length"
          class="no-saved-alerts-msg"
        >
          <b>
            You currently do not have any saved Alert Subscriptions. Create one in the "Create" tab.
          </b>
          <Button
            style="margin-top: 16px"
            @click="newAlertModalVisible = true"
          >
            <i
              class="fas fa-plus-circle"
              style="margin-right: 4px"
            />
            Create Alert Subscription
          </Button>
        </div>
        <Form
          v-else
          ref="alertForm"
          :model="alertForm"
          :label-width="130"
          label-position="right"
        >
          <Row>
            <i-col span="6">
              <FormItem
                prop="region"
                :label-width="0"
              >
                <div style="display: flex">
                  <Select
                    v-if="alertForm.alerts.length"
                    v-model="alertForm.region"
                    placeholder="Region"
                    filterable
                    transfer
                    style="width: 80%"
                    @on-change="value => onHandleRegionChange(value)"
                  >
                    <Option
                      v-for="regionOption in savedAlertFormRegionOptions"
                      :key="regionOption"
                      :value="regionOption"
                      :label="regionOption"
                    >
                      <span>{{ regionOption }}</span>
                      <span
                        v-if="isAlertInvalid(regionOption)"
                        class="invalid-alert-warning"
                        style="float: right; margin-left: 8px"
                      >
                        <i class="fas fa-exclamation-circle" />
                      </span>
                    </Option>
                  </Select>
                  <div class="pending-alert-change-icon">
                    <i
                      v-if="pendingAlertChange"
                      class="fas fa-asterisk"
                    />
                  </div>
                  <Tooltip
                    v-if="isAlertInvalid(alertForm.region)"
                    content="The region name for this alert has either been changed or removed. Please disable or delete this alert."
                    max-width="250"
                    transfer
                    class="invalid-alert-warning"
                    style="margin-left: 8px"
                  >
                    <i
                      class="fas fa-exclamation-circle"
                    />
                  </Tooltip>
                  <Dropdown>
                    <Button
                      type="text"
                      class="context-menu-button"
                    >
                      <i
                        class="fas fa-ellipsis-h"
                        style="font-size: 14px"
                      />
                    </Button>
                    <DropdownMenu slot="list">
                      <DropdownItem
                        @click.native="newAlertModalVisible = true"
                      >
                        Create New Alert
                      </DropdownItem>
                      <DropdownItem
                        @click.native="onDeleteAlertClick"
                      >
                        Delete
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </FormItem>
            </i-col>
            <i-col span="6">
              <FormItem
                prop="email"
                label="Notify me via email"
                :show-message="false"
              >
                <Checkbox
                  v-model="alertForm.email"
                  @on-change="onAlertFormEmailChange"
                />
              </FormItem>
            </i-col>
            <i-col span="6">
              <FormItem
                prop="slack"
                label="Notify me via Slack"
                :show-message="false"
              >
                <Checkbox
                  v-model="alertForm.slack"
                  @on-change="onAlertFormSlackChange"
                />
              </FormItem>
            </i-col>
            <i-col
              v-if="alertForm.alerts.length"
              span="6"
            >
              <FormItem>
                <div style="display: flex; float: right;">
                  <Tooltip
                    content="Test alerts"
                    transfer
                  >
                    <Button
                      :disabled="isTestButtonDisabled"
                      :loading="isTestLoading"
                      type="primary"
                      shape="circle"
                      ghost
                      @click="testAlerts"
                    >
                      Test
                    </Button>
                  </Tooltip>
                  <Tooltip
                    content="Test alerts for all regions (If you want the test to include any changes to the current region settings, you must save it before testing)"
                    transfer
                    max-width="250"
                    style="margin-left: 8px"
                  >
                    <Button
                      :disabled="isTestButtonDisabled"
                      :loading="isTestLoading"
                      type="primary"
                      shape="circle"
                      ghost
                      @click="testAllSavedAlerts"
                    >
                      Test All Regions
                    </Button>
                  </Tooltip>
                </div>
              </FormItem>
            </i-col>
          </Row>
          <AlertTable
            v-model="alertForm.alerts"
            :alert-definitions="alertDefinitionMap"
            :loading="isAlertLoading"
          />
        </Form>
      </div>

      <div slot="footer">
        <Button
          @click="onSavedAlertClearClick"
        >
          Clear Changes
        </Button>
        <Button
          :disabled="isUpdateButtonDisabled"
          :loading="isAlertLoading"
          type="primary"
          @click="onUpdateButtonClick"
        >
          Update
        </Button>
      </div>
    </Modal>
    <Modal
      v-model="newAlertModalVisible"
      title="Create New Alert"
    >
      <div style="margin-bottom: 16px">
        Select a region to subscribe to:
      </div>
      <Select
        v-model="newRegion"
        filterable
        clearable
        placeholder="Select Region"
      >
        <Option
          v-for="option in newRegionOptions"
          :key="option.region"
          :label="option.region"
          :value="option.region"
        />
      </Select>
      <div slot="footer">
        <Button
          @click="onCloseNewAlertModal"
        >
          Cancel
        </Button>
        <Button
          :disabled="!newRegion"
          :loading="isAlertLoading"
          type="primary"
          @click="onCreateRegionAlert(newRegion)"
        >
          Create
        </Button>
      </div>
    </Modal>
    <CustomModal
      v-model="confirmDeleteModalConfig.visible"
      :category="confirmDeleteModalConfig.category"
      :title="confirmDeleteModalConfig.title"
      :message="confirmDeleteModalConfig.message"
      :action-buttons="confirmDeleteModalConfig.actionButtons"
      :cancel-button="confirmDeleteModalConfig.cancelButton"
      mask-closable
    />
    <CustomModal
      v-model="confirmUpdateModalConfig.visible"
      :category="confirmUpdateModalConfig.category"
      :title="confirmUpdateModalConfig.title"
      :message="confirmUpdateModalConfig.message"
      :action-buttons="confirmUpdateModalConfig.actionButtons"
      :cancel-button="confirmUpdateModalConfig.cancelButton"
      mask-closable
    />
  </div>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'

import AlertTable from '@/components/helper-components/AlertTable'
import CustomModal from '@/components/helper-components/CustomModal'

export default {
  components: {
    AlertTable,
    CustomModal
  },

  props: {
    userId: Number
  },

  data () {
    return {
      isAlertOpen: false,
      isAlertLoading: true,
      isTestLoading: true,
      alertDefinitions: [],
      subscriptionPlaceholder: {
        active: false,
        _checked: false, // this is used by iview to set default checkbox checks
        region: '',
        regionKey: '',
        threshold: 0,
        dayPercentage: 0,
        weekPercentage: 0,
        type: 3,
        email: true,
        slack: false
      },
      previousAlerts: [],
      regions: [{ region: '', regionKey: '' }],
      region: '',
      regionKey: '',
      regionAlertMap: {},
      alertForm: {
        alerts: [],
        region: '',
        email: false,
        slack: false
      },
      confirmDeleteModalConfig: {
        visible: false,
        category: 'warning',
        title: 'Delete Alert Subscription Confirmation',
        message: '',
        actionButtons: [{
          label: 'Confirm',
          type: 'warning',
          callback: () => {
            this.deleteCurrentAlertSubscription()
            this.$set(this.confirmDeleteModalConfig, 'visible', false)
          }
        }],
        cancelButton: {
          label: 'Cancel'
        }
      },
      confirmUpdateModalConfig: {
        visible: false,
        category: 'warning',
        title: 'Update Alert Subscription Confirmation',
        message: '',
        actionButtons: [{
          label: 'Confirm',
          type: 'warning',
          callback: () => {
            this.updateAlerts()
            this.$set(this.confirmUpdateModalConfig, 'visible', false)
          }
        }],
        cancelButton: {
          label: 'Cancel'
        }
      },
      newAlertModalVisible: false,
      newRegion: '',
      initialLoading: true
    }
  },

  computed: {
    isTestButtonDisabled () {
      return !this.alertForm.region || !(this.alertForm.email || this.alertForm.slack) || !this.alertForm.alerts.some(alert => alert.active)
    },

    isUpdateButtonDisabled () {
      return !this.alertForm.region
    },

    savedAlertFormRegionOptions () {
      return Object.keys(this.regionAlertMap || {}).map(region => region).sort()
    },

    newRegionOptions () {
      return this.regions.filter(region => !this.regionAlertMap[region.region])
    },

    alertDefinitionMap () {
      return _.keyBy(this.alertDefinitions, 'id')
    },

    alertsPendingChange () {
      const { region } = this.alertForm
      const origAlerts = this.regionAlertMap[region] ? this.regionAlertMap[region].alerts : []
      const origAlertMap = _.keyBy(origAlerts, 'alertId')

      return this.alertForm.alerts.filter(alert => {
        const origAlert = origAlertMap[alert.alertId]

        if (origAlert) {
          const emailChange = origAlert.existsInDb && !!this.alertForm.email !== !!origAlert.email
          const slackChange = origAlert.existsInDb && !!this.alertForm.slack !== !!origAlert.slack
          const activeChange = origAlert.active !== alert.active
          const thresholdChange = origAlert.threshold !== alert.threshold
          const dayPercentageChange = origAlert.dayPercentage !== alert.dayPercentage
          const weekPercentageChange = origAlert.weekPercentage !== alert.weekPercentage
          const typeChange = origAlert.type !== alert.type

          return emailChange || slackChange || activeChange || thresholdChange || dayPercentageChange || weekPercentageChange || typeChange
        }

        return false
      })
    },

    areAllAlertsDisabled () {
      return this.alertForm.alerts.every(alert => !alert.active)
    },

    pendingAlertChange () {
      return this.alertsPendingChange.length > 0
    }
  },

  watch: {
    userId (val) {
      if (val) {
        this.initialize()
      }
    }
  },

  created () {
    if (this.userId) {
      this.initialize()
    }
  },

  methods: {
    /**
     * Initialize alert configuration
     */
    async initialize () {
      this.initialLoading = true
      try {
        const [alertTemplates, alertDefinitions, regions] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_URI}/alerts/subscriptions?userId=${this.userId}`),
          axios.get(`${process.env.VUE_APP_API_URI}/alerts/definitions`),
          axios.get(`${process.env.VUE_APP_API_URI}/alerts/regions`)
        ])
        // this line is kept in case later we will display alertson website
        // this.alertTemplates = alertTemplates.data
        this.alertDefinitions = alertDefinitions.data
        this.regions = regions.data.data
        this.isAlertLoading = true
        this.isTestLoading = true
        this.regionAlertMap = this.createRegionAlertMap(alertTemplates.data)
        if (Object.keys(this.regionAlertMap).length) {
          this.setAlertForm(Object.keys(this.regionAlertMap)[0])
        }

        this.initialLoading = false
        this.isAlertLoading = false
        this.isTestLoading = false
      } catch (error) {
        console.error(error)
        this.$Message.error(`Could not initialize alerting modal ${error}`)
      }
    },

    /**
     * Checks to see if alert is invalid (due to old region name)
     * @param {String} alertRegion - the region that the alert belongs to
     * @return {Boolean}
     */
    isAlertInvalid (alertRegion) {
      return this.regions.findIndex(region => region.region === alertRegion) < 0
    },

    /**
     * Creates an array of default metric alert objects
     * @param {String} region
     * @return {Array}
     */
    createAlertDefaults (region = '') {
      const regionKey = _.get(this.regions.find(regionObj => regionObj.region === region), 'regionKey', null)

      return this.alertDefinitions.map(({ id: alertId }) => ({
        ..._.cloneDeep(this.subscriptionPlaceholder),
        region,
        regionKey,
        alertId: parseInt(alertId)
      }))
    },

    /**
     * Creates a default alert subscription object for a given region
     * @param {String} region
     * @return {Object}
     */
    createDefaultAlertSubscription (region = '') {
      const alerts = this.createAlertDefaults(region)

      return {
        alerts,
        email: alerts.some(alert => alert.email),
        slack: alerts.some(alert => alert.slack)
      }
    },

    /**
     * Mutates the alerts array by replacing a default alert obj with the subscribed alert obj
     * given as input
     * @param {Object} subscribedAlert
     * @param {Array} alerts
     */
    addSubscribedAlert (subscribedAlert, alerts) {
      const alertIndex = alerts.findIndex(
        alert => alert.alertId === subscribedAlert.alertId
      )

      if (alertIndex >= 0) {
        alerts[alertIndex] = {
          ..._.cloneDeep(subscribedAlert),
          _checked: subscribedAlert.active,
          existsInDb: true // flag to indicate the user has saved this alert to the db
        }
      }
    },

    /**
     * Creates an alert subscription map that maps region to an alert
     * subscription object
     * @param {Array} subscribedAlerts
     * @return {Object}
     */
    createRegionAlertMap (subscribedAlerts) {
      const regionAlertMap = {}

      subscribedAlerts.forEach(subscribedAlert => {
        if (!regionAlertMap[subscribedAlert.region]) {
          regionAlertMap[subscribedAlert.region] = this.createDefaultAlertSubscription(subscribedAlert.region)
          regionAlertMap[subscribedAlert.region].email = subscribedAlert.email
          regionAlertMap[subscribedAlert.region].slack = subscribedAlert.slack
        }

        this.addSubscribedAlert(subscribedAlert, regionAlertMap[subscribedAlert.region].alerts)
      })

      return regionAlertMap
    },

    /**
     * Sets the alert form based on the region
     * @param {String} region
     */
    setAlertForm (region) {
      const alertSubscription = this.regionAlertMap[region]
      this.alertForm.region = region
      this.alertForm.alerts = _.cloneDeep(alertSubscription.alerts)
      this.alertForm.email = this.alertForm.alerts.some(alert => alert.existsInDb && alert.email)
      this.alertForm.slack = this.alertForm.alerts.some(alert => alert.existsInDb && alert.slack)
    },

    /**
     * Evoked when clear changes button for the form is clicked
     */
    onSavedAlertClearClick () {
      if (this.alertForm.region) {
        this.setAlertForm(this.alertForm.region)
      } else {
        this.resetAlertForm()
      }
    },

    /**
     * Resets the alert form to the default
     */
    resetAlertForm () {
      const alertDefaults = this.createAlertDefaults()

      this.alertForm.region = ''
      this.alertForm.alerts = alertDefaults
      this.alertForm.email = false
      this.alertForm.slack = false
      this.alertForm.disabled = true
    },

    /**
     * Updates the alert subscription within the alertSubscriptionMap for a given region
     * @param {String} region
     * @param {Array} alerts
     */
    updateRegionAlertMap (region, alerts) {
      let alertSubscription = this.createDefaultAlertSubscription(region)

      if (this.regionAlertMap[region]) {
        alertSubscription = _.cloneDeep(this.regionAlertMap[region])
      }

      alerts.forEach(alert => {
        this.addSubscribedAlert(alert, alertSubscription.alerts)
      })
      this.$set(this.regionAlertMap, region, alertSubscription)
    },

    onUpdateButtonClick () {
      if (this.areAllAlertsDisabled) {
        const confirmMessage = `
          You are about to save the alert settings for <b>${this.alertForm.region}</b> with
          no subscribed alerts. You can subscribe to alerts by checking the box(es) found on
          the left side of the form. Do you want to continue?
        `
        this.$set(this.confirmUpdateModalConfig, 'message', confirmMessage)
        this.$set(this.confirmUpdateModalConfig, 'visible', true)
      } else {
        this.updateAlerts()
      }
    },

    /**
     * Updates the alerts
     */
    async updateAlerts () {
      try {
        this.isAlertLoading = true
        const alertsToBeUpdated = _.cloneDeep(this.alertsPendingChange)
        alertsToBeUpdated.forEach(alert => {
          alert.email = this.alertForm.email
          alert.slack = this.alertForm.slack
        })

        await this.saveAlerts(alertsToBeUpdated)
        this.updateRegionAlertMap(this.alertForm.region, alertsToBeUpdated)
        this.isAlertLoading = false
        this.$Message.success({
          content: `Alert settings for ${this.alertForm.region} has been updated!`,
          duration: 3
        })
      } catch (error) {
        console.error(`Error while updating alerts: ${error.message}`)
        this.$Message.error('Could not update alert subscriptions')
      }
    },

    /**
     * Creates an alert subscription for a region
     * @param {String} region
     */
    async createAlertForRegion (region) {
      try {
        const alerts = this.createAlertDefaults(region)

        this.isAlertLoading = true
        const alertsToBeCreated = [alerts[0]] // only need to save one alert for a region as alert is disabled by default

        await this.saveAlerts(alertsToBeCreated)
        this.updateRegionAlertMap(region, alertsToBeCreated)
        this.isAlertLoading = false
        this.$Message.success({
          content: `Alert subscription for ${region} has been created!`,
          duration: 3
        })
      } catch (error) {
        console.error(`Error while creating alerts for region: ${error.message}`)
        this.$Message.error(`Could not create alert subscription for ${region}`)
      }
    },

    /**
     * Saves the alerts to the database
     * @param {Array} alerts
     */
    async saveAlerts (alerts) {
      try {
        await axios.post(`${process.env.VUE_APP_API_URI}/alerts/subscriptions`, {
          alerts,
          userId: this.userId
        })

        const logPromises = alerts.filter(alert => alert.active)
          .map(alert => {
            const alertLog = { ...alert, metricName: this.alertDefinitionMap[alert.alertId].column }
            return this.$log('alert_configure', alertLog, this.$route.name ? null : { path: this.$route.path }, this.context)
          })
        Promise.all(logPromises)
      } catch (error) {
        console.error(`Error while saving alerts: ${error.message}`)
        throw error
      }
    },

    /**
     * Evoked when the delete alert button is clicked
     */
    onDeleteAlertClick () {
      this.$set(this.confirmDeleteModalConfig, 'message', `Are you sure you want to delete the alert settings for <strong>${this.alertForm.region}</strong>?`)
      this.$set(this.confirmDeleteModalConfig, 'visible', true)
    },

    /**
     * Deletes the currently selected alert subscription
     */
    async deleteCurrentAlertSubscription () {
      try {
        this.isAlertLoading = true
        await axios.delete(`${process.env.VUE_APP_API_URI}/alerts/subscriptions`, {
          params: {
            region: this.alertForm.region,
            userId: this.userId
          }
        })
        this.$delete(this.regionAlertMap, this.alertForm.region)

        const regions = Object.keys(this.regionAlertMap)

        if (regions.length) {
          this.setAlertForm(regions[0])
        } else {
          this.resetAlertForm()
        }

        this.isAlertLoading = false
      } catch (error) {
        this.isAlertLoading = false
        console.error(`Error while deleting alerts: ${error.message}`)
        this.$Message.error('Could not delete alert subscriptions')
      }
    },

    /**
     * Test all of the saved alerts for a user
     */
    async testAllSavedAlerts () {
      this.isTestLoading = true

      try {
        const result = (await axios.post(`${process.env.VUE_APP_API_URI}/alerts/tests/user`, {
          userId: this.userId
        })).data
        const { alertCount } = result
        this.$Message.success(`${alertCount} number of alerts have been triggered and sent`)
        this.isTestLoading = false
      } catch (error) {
        this.isTestLoading = false
        this.$Message.error('Error occurred while testing all saved alerts')
        console.error(`Error while testing saved alerts: ${error.message}`)
      }
    },

    /**
     * Test current alert setting
     */
    async testAlerts () {
      this.isTestLoading = true
      const alerts = this.alertForm.alerts.filter(alert => alert.active)
      alerts.forEach(alert => {
        alert.email = this.alertForm.email
        alert.slack = this.alertForm.slack
        alert.userId = this.userId
      })

      if (!alerts.length) {
        this.isTestLoading = false
        this.$Message.error('No test alert has been configured')
        return
      }

      try {
        const result = await axios.post(`${process.env.VUE_APP_API_URI}/alerts/tests`, {
          alerts
        })
        const { alertCount } = result.data
        this.$Message.success(`${alertCount} number of alerts have been triggered and sent`)
      } catch (error) {
        this.$Message.error(`Could not send alert tests: ${error}`)
        console.error(error)
      }

      this.isTestLoading = false
    },

    /**
     * Handler for when the region select has changed
     * @param {String} region
     */
    onHandleRegionChange (region) {
      this.setAlertForm(region)
    },

    /**
     * Closes the new alert modal
     */
    onCloseNewAlertModal () {
      this.newRegion = ''
      this.newAlertModalVisible = false
    },

    /**
     * Evoked when the create alert button is clicked
     * @param {String} region
     */
    async onCreateRegionAlert (region) {
      await this.createAlertForRegion(region)
      this.setAlertForm(region)
      this.onCloseNewAlertModal()
    },

    onAlertFormEmailChange (email) {
      this.alertForm.alerts.forEach(alert => {
        alert.email = email
      })
    },

    onAlertFormSlackChange (slack) {
      this.alertForm.alerts.forEach(alert => {
        alert.slack = slack
      })
    }
  }
}
</script>

<style scoped>
  .modal-header {
    font-size: 14px;
    color: var(--color-text-content);
  }

  .alert {
    max-height: 500px;
    overflow-y: scroll;
  }

  .action-circle {
    cursor: pointer;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    padding: 8px;
    color: var(--color-text-content);
    border: 1px solid var(--color-gray-border);
    background-color: var(--color-white);
    text-align: center;
    font-size: 14px;
    line-height: 16px;
    margin-right: 10px;
  }

  .ivu-form-item {
    margin-bottom: 16px;
  }

  .ivu-form >>> .ivu-form-item-label {
    font-size: 12px;
  }

  .no-saved-alerts-msg {
    text-align: center;
    margin-top: 15%;
    margin-bottom: 15%;
  }

  .delete-alert-icon {
    cursor: pointer;
    margin-left: 12px;
    font-size: 14px;
  }

  .alert-modal .context-menu-button:focus {
    box-shadow: none;
  }

  .invalid-alert-warning {
    font-size: 16px;
    color: var(--color-red-alert);
  }

  .pending-alert-change-icon {
    margin-left: 4px;
    color: var(--color-red-alert);
  }
</style>
